import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";

// Icons
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

import apiService from "../../services/actions";

/**
 * Retrieve currently logged-in user code for payload modifiedBy
 */
const getActiveUserCode = () => {
  try {
    const loginObj = JSON.parse(localStorage.getItem("loginObj") || "null");
    if (loginObj?.userSession?.userCode) return loginObj.userSession.userCode;
    if (loginObj?.user?.code) return loginObj.user.code;
    if (loginObj?.user?.firstName) return loginObj.user.firstName;
    if (loginObj?.userCode) return loginObj.userCode;
  } catch {}
  try {
    const userDetails = JSON.parse(localStorage.getItem("user_details") || "null");
    if (userDetails?.userSession?.userCode) return userDetails.userSession.userCode;
  } catch {}
  return localStorage.getItem("userCode") || localStorage.getItem("access_name") || "admin";
};

const OrderLabTestsSection = ({
  orderId,
  labPartner: initialLabPartner,
  showSnackbarMessage,
  onCountChange,
  onTestDeleted,
  isTab = false,
}) => {
  const [tests, setTests] = useState([]);
  const [activeTestCount, setActiveTestCount] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [labPartner, setLabPartner] = useState(initialLabPartner || null);
  const [vendorPatientId, setVendorPatientId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Confirmation Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch tests for this orderId
  const fetchTests = useCallback(async () => {
    if (!orderId) {
      setTests([]);
      setActiveTestCount(0);
      setBookingId(null);
      setLabPartner(null);
      setVendorPatientId(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getLabOrdersActiveDetails(orderId);
      if (res?.data?.status === true) {
        const data = res.data.data || {};
        const details = Array.isArray(data.details) ? data.details : [];
        setTests(details);
        const count = data.activeTestCount !== undefined ? data.activeTestCount : details.length;
        setActiveTestCount(count);
        setBookingId(data.bookingId || data.billId || null);
        setLabPartner(data.labPartner || initialLabPartner || null);
        setVendorPatientId(data.vendorPatientId || null);
        if (onCountChange) onCountChange(count);
      } else {
        const msg = res?.data?.message || "No tests found for this order.";
        setTests([]);
        setActiveTestCount(0);
        setBookingId(null);
        setLabPartner(null);
        setVendorPatientId(null);
        setError(msg);
        if (onCountChange) onCountChange(0);
      }
    } catch (err) {
      console.error("Error fetching order lab tests:", err);
      const errMsg = err?.response?.data?.message || "Failed to load lab tests";
      setError(errMsg);
      setTests([]);
      setActiveTestCount(0);
      setBookingId(null);
      setLabPartner(null);
      setVendorPatientId(null);
      if (onCountChange) onCountChange(0);
    } finally {
      setLoading(false);
    }
  }, [orderId, initialLabPartner, onCountChange]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  // Filter tests by search term
  const filteredTests = useMemo(() => {
    if (!searchTerm.trim()) return tests;
    const term = searchTerm.toLowerCase().trim();
    return tests.filter(
      (t) =>
        t.testName?.toLowerCase().includes(term) ||
        t.testCode?.toLowerCase().includes(term) ||
        t.packageName?.toLowerCase().includes(term) ||
        t.packageCode?.toLowerCase().includes(term) ||
        (t.barcodeId && String(t.barcodeId).toLowerCase().includes(term)) ||
        String(t.orderDetailId).includes(term)
    );
  }, [tests, searchTerm]);

  // Open confirmation dialog
  const handleOpenDeleteDialog = (test) => {
    setTestToDelete(test);
    setDeleteDialogOpen(true);
  };

  // Close confirmation dialog
  const handleCloseDeleteDialog = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setTestToDelete(null);
  };

  // Confirm delete / cancel test
  const handleConfirmDelete = async () => {
    if (!testToDelete || !orderId) return;

    setDeleting(true);
    const modifiedBy = getActiveUserCode();
    const currentLabPartner = testToDelete.labPartner || labPartner || initialLabPartner || "PREDLAB";

    try {
      const payload = {
        orderId: String(orderId),
        orderDetailId: Number(testToDelete.orderDetailId),
        labPartner: String(currentLabPartner),
        modifiedBy: String(modifiedBy),
        isRefund: 0,
        deductAmount: 0,
      };

      const res = await apiService.deleteLabOrderTest(payload);

      if (res?.status === 200 || res?.data?.status === true || res?.data?.data?.success === true) {
        const successMsg =
          res?.data?.message ||
          res?.data?.data?.message ||
          `Test "${testToDelete.testName || testToDelete.testCode}" cancelled successfully.`;
        if (showSnackbarMessage) {
          showSnackbarMessage(successMsg, "success");
        }
        setDeleteDialogOpen(false);
        setTestToDelete(null);

        // Re-fetch tests list
        await fetchTests();

        if (onTestDeleted) {
          onTestDeleted(testToDelete);
        }
      } else {
        const errMsg = res?.data?.message || res?.data?.data?.message || "Failed to cancel test. Please try again.";
        if (showSnackbarMessage) {
          showSnackbarMessage(errMsg, "error");
        }
      }
    } catch (err) {
      console.error("Error cancelling test:", err);
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Error cancelling test. Please try again.";
      if (showSnackbarMessage) {
        showSnackbarMessage(errMsg, "error");
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", ...(isTab ? { pt: 1 } : {}) }}>
      {/* Top Header / Action Bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {isTab && (
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#1e293b" }}>
              Order Tests
            </Typography>
          )}
          {activeTestCount !== null && (
            <Chip
              label={`${activeTestCount} Active Test${activeTestCount === 1 ? "" : "s"}`}
              size="small"
              color={activeTestCount > 0 ? "primary" : "default"}
              sx={{ fontWeight: 600, fontSize: "11px", height: 22 }}
            />
          )}
          {labPartner && (
            <Chip
              label={`Partner: ${labPartner}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: "11px", height: 22 }}
            />
          )}
          {bookingId && (
            <Chip
              label={labPartner === "PREDLAB" ? `Bill ID: ${bookingId}` : `Booking ID: ${bookingId}`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: "11px", height: 22, color: "#64748b" }}
            />
          )}
          {vendorPatientId && (
            <Chip
              label={`Patient ID: ${vendorPatientId}`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: "11px", height: 22, color: "#64748b" }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {tests.length > 2 && (
            <TextField
              size="small"
              placeholder="Search tests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                sx: { height: 32, fontSize: "0.8125rem", width: 170 },
              }}
            />
          )}

          <Tooltip title="Refresh Tests">
            <span>
              <IconButton
                size="small"
                onClick={fetchTests}
                disabled={loading}
                sx={{
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  p: 0.6,
                  "&:hover": { backgroundColor: "#f1f5f9" },
                }}
              >
                <RefreshOutlinedIcon
                  fontSize="small"
                  sx={{
                    animation: loading ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": { "100%": { transform: "rotate(360deg)" } },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Loading Indicator */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 1 }}>
          <CircularProgress size={28} />
          <Typography variant="caption" color="text.secondary">
            Loading lab tests...
          </Typography>
        </Box>
      ) : error && tests.length === 0 ? (
        /* Error or Empty Alert */
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={fetchTests}>
              Retry
            </Button>
          }
          sx={{ mb: 1.5 }}
        >
          {error}
        </Alert>
      ) : tests.length === 0 ? (
        /* Empty State */
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            backgroundColor: "#ffffff",
            borderRadius: 1,
            border: "1px dashed #cbd5e1",
          }}
        >
          <ScienceOutlinedIcon sx={{ fontSize: 36, color: "#94a3b8", mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            No lab tests found for this order.
          </Typography>
        </Box>
      ) : (
        /* Tests Table */
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 380,
            boxShadow: "none",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#f8fafc",
                    width: 36,
                    fontSize: "0.75rem",
                    color: "#475569",
                  }}
                >
                  #
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#f8fafc",
                    fontSize: "0.75rem",
                    color: "#475569",
                  }}
                >
                  Test Details
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#f8fafc",
                    fontSize: "0.75rem",
                    color: "#475569",
                  }}
                >
                  Package
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#f8fafc",
                    fontSize: "0.75rem",
                    color: "#475569",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#f8fafc",
                    width: 70,
                    fontSize: "0.75rem",
                    color: "#475569",
                    whiteSpace: "nowrap",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: "#94a3b8" }}>
                    No tests matching &quot;{searchTerm}&quot;
                  </TableCell>
                </TableRow>
              ) : (
                filteredTests.map((test, idx) => {
                  const isDeletable =
                    test.deletable !== undefined
                      ? Boolean(test.deletable)
                      : test.cancellable !== false;
                  return (
                    <TableRow
                      key={`lab-test-${test.orderDetailId || test.testCode || idx}`}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      {/* Index */}
                      <TableCell sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                        {idx + 1}
                      </TableCell>

                      {/* Test Name & Code */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          sx={{ color: "#1e293b", fontSize: "0.84rem" }}
                        >
                          {test.testName || test.testCode || "Unknown Test"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3, flexWrap: "wrap" }}>
                          {test.testCode && (
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                              Code: <strong>{test.testCode}</strong>
                            </Typography>
                          )}
                          {test.orderDetailId && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#94a3b8",
                                borderLeft: "1px solid #cbd5e1",
                                pl: 0.8,
                              }}
                            >
                              ID: {test.orderDetailId}
                            </Typography>
                          )}
                          {test.barcodeId && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#94a3b8",
                                borderLeft: "1px solid #cbd5e1",
                                pl: 0.8,
                              }}
                            >
                              Barcode: {test.barcodeId}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Package Name / Code */}
                      <TableCell>
                        {test.packageName || test.packageCode ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ color: "#334155", fontSize: "0.8125rem" }}
                            >
                              {test.packageName || "—"}
                            </Typography>
                            {test.packageCode && (
                              <Typography variant="caption" sx={{ color: "#64748b" }}>
                                {test.packageCode}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Individual Test
                          </Typography>
                        )}
                        {test.packageComponent && (
                          <Box sx={{ mt: 0.3 }}>
                            <Chip
                              label="Package Component"
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{
                                height: 18,
                                fontSize: "10px",
                                fontWeight: 600,
                                px: 0.3,
                              }}
                            />
                          </Box>
                        )}
                      </TableCell>

                      {/* Cancellable Status */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {isDeletable ? (
                          <Chip
                            icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                            label="Cancellable"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: "11px",
                              fontWeight: 600,
                              borderColor: "#86efac",
                              backgroundColor: "#f0fdf4",
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<BlockOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                            label="Non-Cancellable"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: "11px",
                              fontWeight: 600,
                              backgroundColor: "#f8fafc",
                            }}
                          />
                        )}
                      </TableCell>

                      {/* Action: Delete */}
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip
                          title={
                            isDeletable
                              ? "Delete / Cancel Test"
                              : "This test cannot be cancelled"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!isDeletable || deleting}
                              onClick={() => handleOpenDeleteDialog(test)}
                              sx={{
                                p: 0.6,
                                "&:hover": { backgroundColor: "#fee2e2" },
                                "&.Mui-disabled": { opacity: 0.35 },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation Dialog Before Deletion */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 30000 }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            pb: 1,
            color: "#b91c1c",
            fontWeight: "bold",
          }}
        >
          <WarningAmberRoundedIcon color="error" sx={{ fontSize: 26 }} />
          Confirm Delete Lab Test
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ color: "#334155", mb: 2 }}>
            Are you sure you want to delete and cancel this test from Order #
            <strong>{orderId}</strong>?
          </DialogContentText>

          {testToDelete && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: "#fff1f2",
                border: "1px solid #fecdd3",
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  TEST NAME
                </Typography>
                <Typography variant="body2" fontWeight="700" color="#9f1239">
                  {testToDelete.testName || "—"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.8 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    TEST CODE
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {testToDelete.testCode || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    DETAIL ID
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {testToDelete.orderDetailId}
                  </Typography>
                </Box>
              </Box>

              {(testToDelete.labPartner || labPartner) && (
                <Box sx={{ mb: 0.8 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    LAB PARTNER
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {testToDelete.labPartner || labPartner}
                  </Typography>
                </Box>
              )}

              {testToDelete.barcodeId && (
                <Box sx={{ mb: 0.8 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    BARCODE
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {testToDelete.barcodeId}
                  </Typography>
                </Box>
              )}

              {testToDelete.packageName && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="600">
                    PACKAGE
                  </Typography>
                  <Typography variant="body2">
                    {testToDelete.packageName} ({testToDelete.packageCode})
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1.5 }}>
            * This action will cancel the test in {testToDelete?.labPartner || labPartner || "the lab"}. This cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCloseDeleteDialog}
            disabled={deleting}
            sx={{ textTransform: "none", color: "#475569", borderColor: "#cbd5e1" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon fontSize="small" />
              )
            }
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {deleting ? "Deleting..." : "Confirm Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

OrderLabTestsSection.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labPartner: PropTypes.string,
  showSnackbarMessage: PropTypes.func,
  onCountChange: PropTypes.func,
  onTestDeleted: PropTypes.func,
  isTab: PropTypes.bool,
};

export default OrderLabTestsSection;
